import React, { Suspense } from "react";
import { Route, Routes, useLocation } from "react-router-dom";

import Sidebar from "./components/organisms/Sidebar";
import Signin from "./screens/Signin";
import Dashboard from "./screens/Dashboard";
import ErrorBoundary from "./components/organisms/ErrorBoundary";
import Loader from "./components/molecules/Loader";
import Resources from "./screens/Resources";
import AdminPanel from "./screens/AdminPanel";
import SultanSignin from "./screens/SultanSignin";
import SultanChooseHosp from "./screens/SultanChooseHosp";
import { useHospDocData } from "./lib/contexts/HospitalDoctorContext";
import CreatePrescription from "./screens/CreatePrescription";
import Analytics from "./screens/Analytics";
import Profile from "./screens/Profile";
import BulkTesting from "./screens/BulkTesting";
import DailyReportPage from "./screens/DailyReportPage";
import DownloadCenter from "./screens/DownloadCenter";
import DownloadManager from "./screens/DownloadManager";
import PublicBranchGate from "./screens/PublicBranchGate";
import PublicLanding from "./screens/PublicLanding";
import PublicAIChat from "./screens/PublicAIChat";
import PublicBookingAction from "./screens/PublicBookingAction";
import PublicGo from "./screens/PublicGo";
import { isPublicBookingHost } from "./lib/utils/publicHost";
import { SidebarProvider } from "./lib/contexts/SidebarContext";

// importing pages lazily so that when a user hits that route, it dowunloads that page only
const Doctors = React.lazy(() => import("./screens/Doctors"));
const AppointmentPrograms = React.lazy(
  () => import("./screens/AppointmentPrograms")
);
const EachDoctor = React.lazy(() => import("./screens/EachDoctor"));
const DoctorQueueV2 = React.lazy(
  () => import("./screens/doctorQueueV2/DoctorQueueV2")
);
const AppointmentsV2 = React.lazy(
  () => import("./screens/appointmentsV2/AppointmentsV2")
);
const Appointments = React.lazy(() => import("./screens/Appointments"));
const Settings = React.lazy(() => import("./screens/Settings"));
const DocDetails = React.lazy(() => import("./screens/DocDetails"));
const Patients = React.lazy(() => import("./screens/Patients"));
const ChooseHosp = React.lazy(() => import("./screens/ChooseHosp"));
const Reports = React.lazy(() => import("./screens/Reports"));
const AppointmentBooking = React.lazy(
  () => import("./screens/AppointmentBooking")
);
const Relations = React.lazy(() => import("./screens/Relations"));
const Support = React.lazy(() => import("./screens/Support"));
const WhatsNew = React.lazy(() => import("./screens/WhatsNew"));
const RoomManagement = React.lazy(() => import("./screens/RoomManagement"));
const ButtonManagement = React.lazy(() => import("./screens/ButtonManagement"));
const TVConfiguration = React.lazy(() => import("./screens/TVConfiguration"));
const WaLeads = React.lazy(() => import("./screens/WaLeads"));

const AppRoutes = () => {
  const location = useLocation();
  const { hospitalID } = useHospDocData();

  if (isPublicBookingHost()) {
    return (
      <Suspense fallback={<Loader hospitalId={hospitalID} />}>
        <Routes>
          <Route path="/" element={<PublicLanding />} />
          <Route path="/go/:booking_id/:kind/:sig" element={<PublicGo />} />
          <Route path="/:hospital_id/ai" element={<PublicAIChat />} />
          {/* The booking's own pages, opened from the WhatsApp confirmation card. */}
          <Route path="/:hospital_id/status/:booking_id" element={<PublicBookingAction mode="status" />} />
          <Route path="/:hospital_id/manage/:booking_id" element={<PublicBookingAction mode="manage" />} />
          <Route path="/:hospital_id" element={<PublicBranchGate />} />
          <Route path="*" element={<PublicLanding />} />
        </Routes>
      </Suspense>
    );
  }

  return (
    <SidebarProvider>
    <div className="flex flex-col lg:flex-row h-screen overflow-hidden">
      {location.pathname !== "/" &&
        location.pathname !== "/admin" &&
        location.pathname !== "/sultan/login" && <Sidebar />}
      <ErrorBoundary>
        <Suspense fallback={<Loader hospitalId={hospitalID} />}>
          <Routes>
            <Route path="/" element={<Signin />} />
            <Route path="sultan/login" element={<SultanSignin />} />
            <Route path="admin" element={<ChooseHosp />} />
            <Route path="sultanadmin" element={<SultanChooseHosp />} />
            <Route path=":hospital_id">
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="resources" element={<Resources />} />
              <Route path="profile" element={<Profile />} />
              <Route path="analytics" element={<Analytics />} />
              <Route path="downloadcenter" element={<DownloadCenter />} />
              <Route path="download-center" element={<DownloadCenter />} />
              <Route path="downloadmanager" element={<DownloadManager />} />
              <Route path="adminPanel" element={<AdminPanel />} />
              <Route path="daily-report" element={<DailyReportPage />} />
              <Route path="whatsnew" element={<WhatsNew />} />
              <Route
                path="whatsnew/:feature_id"
                element={React.createElement(
                  React.lazy(() => import("./screens/WhatsNewDetail"))
                )}
              />
              <Route path="categories" element={<AppointmentPrograms />} />
              <Route path="doctors" element={<Doctors />} />
              <Route path="doctors/doc/:mapping_id" element={<EachDoctor />} />
              <Route
                path="doctors/v2/:mapping_id"
                element={<DoctorQueueV2 />}
              />
              <Route path="doctors/:mapping_id" element={<DocDetails />} />

              <Route path="appointments" element={<Appointments />} />
              <Route path="appointments/v2" element={<AppointmentsV2 />} />
              <Route
                path="appointments/book-an-appointment"
                element={<AppointmentBooking />}
              />

              <Route path="patients" element={<Patients />} />
              <Route path="patients/:patient_id" element={<Reports />} />
              <Route
                path="patients/relations/:patient_id"
                element={<Relations />}
              />

              <Route path="settings" element={<Settings />} />
              <Route path="settings/rooms" element={<RoomManagement />} /> 
              <Route path="settings/buttons" element={<ButtonManagement />} />
              <Route path="settings/tvconfig" element={<TVConfiguration />} />
              <Route
                path="createprescription"
                element={<CreatePrescription />}
              />
              <Route path="waleads" element={<WaLeads />} />
              <Route path="support" element={<Support />} />
              <Route path="testing" element={<BulkTesting />} />
            </Route>
          </Routes>
        </Suspense>
      </ErrorBoundary>
    </div>
    </SidebarProvider>
  );
};

export default AppRoutes;
